import { GoalLandingPagePreviewVideoModel, GoalLandingPageVideoProvider } from '@models/goals';

export function detectVideoProvider(url: string): GoalLandingPageVideoProvider | null {
  if (/(?:youtube\.com|youtu\.be)/i.test(url)) return GoalLandingPageVideoProvider.YOUTUBE;
  if (/vimeo\.com/i.test(url)) return GoalLandingPageVideoProvider.VIMEO;
  return null;
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /youtu\.be\/([\w-]{6,})/i,
    /youtube\.com\/watch\?[^#]*\bv=([\w-]{6,})/i,
    /youtube\.com\/embed\/([\w-]{6,})/i,
    /youtube\.com\/shorts\/([\w-]{6,})/i
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function extractVimeoId(url: string): string | null {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return match ? match[1] : null;
}

function resolveProvider(
  video: GoalLandingPagePreviewVideoModel | null | undefined
): GoalLandingPageVideoProvider | null {
  if (!video?.url) return null;
  return video.provider ?? detectVideoProvider(video.url);
}

// Minimalistic chrome: hide related-video grid, annotations, and (where the
// provider still honors it) branding. YouTube no longer lets an iframe fully
// remove its title bar / "Watch on YouTube" overlay via params — for a truly
// minimal look the UI shows a click-to-play facade (see getYouTubeVideoId /
// getYouTubeThumbnailUrl) and only mounts the iframe once the user hits play.
const YOUTUBE_EMBED_PARAMS = 'rel=0&modestbranding=1&iv_load_policy=3&playsinline=1';
const VIMEO_EMBED_PARAMS = 'title=0&byline=0&portrait=0';

/**
 * Builds an embeddable iframe URL for a preview video, or null when the url is
 * missing/unrecognized. Uses the stored provider when present, otherwise detects
 * it from the url. Pass autoplay=true once the user has clicked play.
 */
export function getVideoEmbedUrl(
  video: GoalLandingPagePreviewVideoModel | null | undefined,
  autoplay = false
): string | null {
  const provider = resolveProvider(video);
  if (provider === GoalLandingPageVideoProvider.YOUTUBE) {
    const id = extractYouTubeId(video!.url);
    if (!id) return null;
    const params = autoplay ? `${YOUTUBE_EMBED_PARAMS}&autoplay=1` : YOUTUBE_EMBED_PARAMS;
    return `https://www.youtube-nocookie.com/embed/${id}?${params}`;
  }
  if (provider === GoalLandingPageVideoProvider.VIMEO) {
    const id = extractVimeoId(video!.url);
    if (!id) return null;
    const params = autoplay ? `${VIMEO_EMBED_PARAMS}&autoplay=1` : VIMEO_EMBED_PARAMS;
    return `https://player.vimeo.com/video/${id}?${params}`;
  }
  return null;
}

/** The YouTube video id when the preview video resolves to YouTube, else null. */
export function getYouTubeVideoId(
  video: GoalLandingPagePreviewVideoModel | null | undefined
): string | null {
  if (resolveProvider(video) !== GoalLandingPageVideoProvider.YOUTUBE) return null;
  return extractYouTubeId(video!.url);
}

export type YouTubeThumbnailQuality = 'maxresdefault' | 'hqdefault';

/** Poster image for the click-to-play facade (served from i.ytimg.com). */
export function getYouTubeThumbnailUrl(
  videoId: string,
  quality: YouTubeThumbnailQuality = 'maxresdefault'
): string {
  return `https://i.ytimg.com/vi/${videoId}/${quality}.jpg`;
}
